/**
 * Heures approximatives (décision du propriétaire du 15 octobre 2026) : on ne
 * connaît pas l'heure exacte, mais on sait qu'autour de cette heure-là il y aura
 * quelque chose. Une intervention, un temps bloqué ou une tâche planifiée porte
 * alors une marge autour de son début.
 *
 * Le créneau enregistré reste la référence des conflits et de l'export ; la
 * marge ne change que la façon de l'écrire :
 * - dans l'application : « ≈ 14:00 », et « vers 14:00 (± 1 h) » en détail ;
 * - pour un client : « vers 14 h (entre 13 h et 15 h) », jamais une heure exacte.
 */
import { z } from 'zod';

export const TIME_APPROX_MINUTES = [30, 60, 120] as const;
export type TimeApproxMinutes = (typeof TIME_APPROX_MINUTES)[number];

/** Marge reçue par l'API : une des trois proposées, ou `null` pour une heure exacte. */
export const timeApproxSchema = z
  .union([z.literal(30), z.literal(60), z.literal(120)], {
    error: 'Marge invalide : ± 30 min, ± 1 h ou ± 2 h.',
  })
  .nullable();

export function isTimeApproxMinutes(value: unknown): value is TimeApproxMinutes {
  return (TIME_APPROX_MINUTES as readonly unknown[]).includes(value);
}

/** « ± 30 min », « ± 1 h », « ± 2 h ». */
export function approxMarginLabel(minutes: TimeApproxMinutes | number): string {
  return minutes < 60 ? `± ${minutes} min` : `± ${minutes / 60} h`;
}

/** « 14:00 » dans le fuseau donné (celui du navigateur sans fuseau). */
function clock(date: Date, timeZone?: string): string {
  return date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  });
}

/** « 14 h », « 14 h 30 » : l'heure telle qu'on l'écrit à un client. */
export function hourFr(date: Date, timeZone?: string): string {
  const [hours = '0', minutes = '00'] = clock(date, timeZone).split(':');
  return `${Number(hours)} h${minutes === '00' ? '' : ` ${minutes}`}`;
}

/**
 * Heure courte d'un créneau : « 14:00 », ou « ≈ 14:00 » si elle est approximative.
 * `minutes` absent ou nul : heure exacte.
 */
export function approxClock(
  startsAt: string | Date,
  minutes: number | null | undefined,
  timeZone?: string,
): string {
  const time = clock(new Date(startsAt), timeZone);
  return minutes ? `≈ ${time}` : time;
}

/** Détail interne : « vers 14:00 (± 1 h) », ou « 14:00 » si l'heure est exacte. */
export function approxClockDetail(
  startsAt: string | Date,
  minutes: number | null | undefined,
  timeZone?: string,
): string {
  const time = clock(new Date(startsAt), timeZone);
  return minutes ? `vers ${time} (${approxMarginLabel(minutes)})` : time;
}

/**
 * Pour un client : « vers 14 h (entre 13 h et 15 h) ». Une heure exacte reste
 * « à 14 h ». Sert aux e-mails, SMS et au portail : un client ne lit jamais une
 * heure précise quand l'entreprise ne l'a pas donnée.
 */
export function customerTimeFr(
  startsAt: string | Date,
  minutes: number | null | undefined,
  timeZone?: string,
): string {
  const at = new Date(startsAt);
  if (!minutes) return `à ${hourFr(at, timeZone)}`;
  const from = new Date(at.getTime() - minutes * 60_000);
  const to = new Date(at.getTime() + minutes * 60_000);
  return `vers ${hourFr(at, timeZone)} (entre ${hourFr(from, timeZone)} et ${hourFr(to, timeZone)})`;
}
